import React, { useEffect, useState } from 'react'
import { Col, Dropdown, Row, Tab, Tabs } from 'react-bootstrap'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { useImmer } from 'use-immer'
import ShimmeringEffect from '../../components/shimmering/ShimmeringEffect'
import {
  renderTooltipContent,
  replaceString
} from '../../lib/AllGlobalFunction'
import { isSellerPanel } from '../../lib/AllStaticVariables'
import axiosProvider from '../../lib/AxiosProvider'

const ProductChart = ({ values, setFieldValue }) => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('order')
  const [data, setData] = useImmer({
    product: {},
    productData: [],
    productChart: []
  })

  const getProductColor = (key) => {
    let color = ''
    switch (key) {
      case 'active':
        color = '#46dfaf'
        break

      case 'inBulkUpload':
        color = '#6c1aac'
        break

      case 'inExisting':
        color = '#2c799e'
        break

      case 'inRequest':
        color = '#41d1bc'
        break

      case 'inactivate':
        color = '#a368ed'
        break

      case 'totalActiveStocks':
        color = '#3aa1d7'
        break

      case 'totalStocks':
        color = '#205d92'
        break

      case 'unique':
        color = '#c3cdeb'
        break

      default:
        break
    }
    return color
  }

  const fetchData = async (days = 'All') => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'GET',
        endpoint: isSellerPanel
          ? 'Seller/Dashboard/getProductCounts'
          : 'Dashboard/getProductCounts',
        queryString: `?days=${days}`
      })

      const productChart = await axiosProvider({
        method: 'GET',
        endpoint: isSellerPanel
          ? 'Seller/Dashboard/getProductChart'
          : 'Dashboard/getProductChart',
        queryString: `?ReportDays=${
          days === 'Today' ? '1' : days === 'All' ? '365' : days
        }`
      })

      const orderChart = await axiosProvider({
        method: 'GET',
        endpoint: isSellerPanel
          ? 'Seller/Dashboard/getOrderChart'
          : 'Dashboard/getOrderChart',
        queryString: `?ReportDays=${
          days === 'Today' ? '1' : days === 'All' ? '365' : days
        }`
      })

      setLoading(false)

      if (response?.status === 200) {
        let productData = Object.entries(response?.data?.data).reduce(
          (arr, [key, value]) => {
            if (key !== 'total') {
              arr.push({
                name: replaceString(key),
                value,
                color: getProductColor(key)
              })
            }
            return arr
          },
          []
        )
        setData((draft) => {
          draft.product = response?.data?.data
          draft.productData = productData
        })
      }

      if (productChart?.status === 200) {
        setData((draft) => {
          draft.productChart = productChart?.data?.data
        })
      }

      if (orderChart?.status === 200) {
        setData((draft) => {
          draft.orderChart = orderChart?.data?.data
        })
      }
    } catch {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return loading ? (
    <ShimmeringEffect />
  ) : (
    <div
      style={{ boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
      className='pv-dashbord-chart-col pv-rounded-chart rounded h-100'
    >
      <Tabs
        defaultActiveKey='order'
        id='justify-tab-example'
        className='mb-3'
        justify
      >
        <Tab className='border-0 ' eventKey='order' title='Sales'>
          <ResponsiveContainer width='100%' height={500}>
            <AreaChart
              data={data?.orderChart}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#82ca9d' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#82ca9d' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' />
              <YAxis />
              <CartesianGrid strokeDasharray='2 2' />
              <Tooltip content={renderTooltipContent} />
              <Area
                type='monotone'
                dataKey='monthStats'
                stroke='#8884d8'
                fillOpacity={1}
                fill='url(#colorUv)'
              />
              <Area
                type='monotone'
                dataKey='monthDetails'
                stroke='#82ca9d'
                fillOpacity={1}
                fill='url(#colorPv)'
              />
            </AreaChart>
          </ResponsiveContainer>
        </Tab>

        <Tab className='border-0 ' eventKey='profile' title='Products'>
          <Row className='w-100 m-auto pv-PercentArea-desc'>
            <Col className='border text-center p-2'>
              <div>
                <h6 className='medium mb-1'>Total Products</h6>
              </div>
              <div>
                <h5 className='mb-0 bold'>{data?.product?.total ?? 0}</h5>
              </div>
            </Col>
            <Col className='border text-center p-1'>
              <div>
                <h6 className='medium mb-1'>Unique Products</h6>
              </div>
              <div>
                <h5 className='mb-0 bold'>{data?.product?.unique ?? 0}</h5>
              </div>
            </Col>
            <Col className='border text-center p-1'>
              <div>
                <h6 className='medium mb-1'>In Existing</h6>
              </div>
              <div>
                <h5 className='mb-0 bold'>
                  {data?.product?.inExisting ?? 0}
                </h5>
              </div>
            </Col>
            <Col className='border text-center p-1'>
              <div>
                <h6 className='medium mb-1'>In Inactivate</h6>
              </div>
              <div>
                <h5 className='mb-0 bold'>
                  {data?.product?.inactivate ?? 0}
                </h5>
              </div>
            </Col>
            <Col className='border text-center p-1'>
              <div>
                <h6 className='medium mb-1'>Active Products</h6>
              </div>
              <div>
                <h5 className='mb-0 bold'>{data?.product?.active ?? 0}</h5>
              </div>
            </Col>
          </Row>
          <ResponsiveContainer width='100%' height={400}>
            <AreaChart
              data={data?.productChart}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorPv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#82ca9d' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#82ca9d' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' />
              <YAxis />
              <CartesianGrid strokeDasharray='2 2' />
              <Tooltip content={renderTooltipContent} />
              <Area
                type='monotone'
                dataKey='monthStats'
                stroke='#8884d8'
                fillOpacity={1}
                fill='url(#colorUv)'
              />
              <Area
                type='monotone'
                dataKey='monthDetails'
                stroke='#82ca9d'
                fillOpacity={1}
                fill='url(#colorPv)'
              />
            </AreaChart>
          </ResponsiveContainer>
          <Row className='w-100 m-auto pv-PercentArea-desc'>
            <Col className='border text-center p-1' md={4}>
              <h5 className='medium mb-1'>Bulk Upload & In Approval</h5>
              <h5 className='mb-0 bold'>{data?.product?.inBulkUpload ?? 0}</h5>
            </Col>
            <Col className='border text-center p-1' md={4}>
              <h5 className='medium mb-1'>Total Stocks</h5>
              <h5 className='mb-0 bold'>{data?.product?.totalStocks ?? 0}</h5>
            </Col>
            <Col className='border text-center p-1' md={4}>
              <h5 className='medium mb-1'>Total Active Stocks</h5>
              <h5 className='mb-0 bold'>
                {data?.product?.totalActiveStocks ?? 0}
              </h5>
            </Col>
          </Row>
        </Tab>
      </Tabs>
      <Dropdown className='pv-chartdash-dropbtn'>
        <Dropdown.Toggle className='btn-light' id='dropdown-basic'>
          {values?.product ? values?.product : 'All'}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => {
              fetchData('All')
              setFieldValue('product', 'All')
            }}
          >
            All
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('Today')
              setFieldValue('product', 'Today')
            }}
          >
            Today
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('7')
              setFieldValue('product', 'Last Week')
            }}
          >
            Last Week
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('15')
              setFieldValue('product', 'Last 15 Days')
            }}
          >
            Last 15 Days
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('30')
              setFieldValue('product', 'This Month')
            }}
          >
            This Month
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('90')
              setFieldValue('product', 'Last 3 Months')
            }}
          >
            Last 3 Months
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

export default ProductChart
